// TileDrawer — Slide-In Drawer fuer Bento-Tile-Drilldown.
//
// bento-spec-sprint6.md "Drilldown-Drawer":
//   - Slide-In rechts 480px Desktop, full-width Mobile
//   - Backdrop-Klick + Esc + X-Button schliessen
//   - body-scroll-lock waehrend offen
//   - Focus-Trap simpel: initial focus auf X-Button
//
// Generic — Inhalt kommt als children. Drawer-Content-Components
// leben in components/bento/drawers/.

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** ARIA-Labelled-By id; default leitet sich aus title ab. */
  ariaLabel?: string;
}

export default function TileDrawer({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  ariaLabel,
}: Props) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    // Focus auf Close-Button (simple Focus-Trap fuer A11y)
    requestAnimationFrame(() => closeBtnRef.current?.focus());
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Drawer schliessen"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity"
        tabIndex={-1}
      />

      {/* Panel — Slide-In von rechts (CSS-Animation auf Mount) */}
      <aside
        className="relative h-full w-full sm:w-[480px] max-w-full bg-slate-950 border-l border-slate-700/60 shadow-2xl flex flex-col bento-drawer-slide-in"
      >
        <header className="flex items-start justify-between gap-2 px-5 py-4 border-b border-slate-800/60 shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-100">
              {title}
            </h2>
            {subtitle && (
              <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800 active:bg-slate-700 shrink-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-5 w-5"
              aria-hidden
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="6" y1="18" x2="18" y2="6" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {children}
        </div>
      </aside>
    </div>
  );
}
